import React from 'react';
import type { AudienceQfaQuestion } from '@mentimeter/http-clients';
import { useRagnar } from '@mentimeter/ragnar-react';
import { ThumbsUpReaction } from '@mentimeter/ragnar-visuals';
import { Avatar } from '@mentimeter/ragnar-ui/avatar';
import { Badge } from '@mentimeter/ragnar-ui/badge';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import { getNameInitials } from '../../utils/getNameInitials';
import { useTrack } from '../../utils/tracker';
import { ReactionAnimation } from '../../ui';

interface Props {
  question: AudienceQfaQuestion;
  hasUpvoted: boolean;
  onToggleUpvote: (id: number) => void;
  isUserQuestion: boolean;
  index: number;
}

function UpvoteButton({
  highlight,
  onClick,
  label,
}: {
  highlight: boolean;
  onClick: () => void;
  label: string;
}) {
  const { theme } = useRagnar();
  return (
    <ReactionAnimation
      enable={!highlight}
      vertical={false}
      themeIndex={0}
      reaction="thumbsup"
    >
      <Clickable
        py="space2"
        px="space5"
        borderRadius="full"
        borderWidth="2px"
        borderStyle="solid"
        borderColor={highlight ? 'secondary' : 'neutralWeak'}
        bg={highlight ? 'secondaryWeak' : 'neutralWeak'}
        onClick={onClick}
        aria-label={label}
        extend={() => ({
          cursor: 'pointer',
        })}
      >
        <ThumbsUpReaction
          size={4}
          color={
            highlight
              ? theme.colors.onSecondaryWeak
              : theme.colors.onNeutralWeakest
          }
        />
      </Clickable>
    </ReactionAnimation>
  );
}

const Separator = () => (
  <Box
    borderColor="borderWeak"
    borderStyle="solid"
    borderTopWidth="1px"
    mx="space4"
  />
);

export const QfaItem = ({
  onToggleUpvote,
  hasUpvoted,
  question: { id, question, upvotes, display_name },
  isUserQuestion,
  index,
}: Props) => {
  const trackVoter = useTrack();
  const translate = useTranslations('participating.qfa');

  const showBadge = isUserQuestion && display_name === null;

  return (
    <>
      {index !== 0 && <Separator />}
      <Box
        as="li"
        key={id}
        flexDirection="row"
        py="space6"
        px="space4"
        overflow="visible"
        justifyContent="space-between"
        alignItems="stretch"
        width="100%"
        gap="space4"
      >
        {display_name && (
          <Box>
            <Avatar
              type="user"
              initials={getNameInitials(display_name, 1)}
              name={display_name}
            />
          </Box>
        )}
        <Box flex="1" gap="space1">
          {display_name && (
            <Text as="p" color="textWeak">
              {display_name}
            </Text>
          )}
          {showBadge && (
            <Badge
              type="text"
              text={translate('your_question_badge_label')}
              bg="infoWeak"
              color="onInfoWeak"
              textTransform="lowercase"
            />
          )}
          <Text as="p" lineHeight="150" color="text">
            {question}
          </Text>
        </Box>

        <Box justifyContent="center" alignItems="center" gap="space2">
          <UpvoteButton
            highlight={hasUpvoted}
            label={
              hasUpvoted
                ? translate('downvote_on_question_a11y', { question })
                : translate('upvote_on_question_a11y', { question })
            }
            onClick={() => {
              trackVoter({ event: 'Upvoted QFA' });
              onToggleUpvote(id);
            }}
          />

          <Text
            fontWeight="semiBold"
            aria-label={translate('number_of_upvotes_for_question_a11y', {
              upvotes,
              question,
            })}
          >
            {upvotes}
          </Text>
        </Box>
      </Box>
    </>
  );
};
